import { articleTime } from './blog';

export const INDEXABLE_ROBOTS =
  'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
export const HIDDEN_ROBOTS = 'noindex, follow';

export interface PageMetadata {
  title: string;
  description: string;
  url: string;
  image?: string;
  type?: 'website' | 'article';
  schema?: object;
  noindex?: boolean;
  language?: string;
  alternates?: { language: string; url: string }[];
}

/** One owner replaces the previous page metadata, including legacy template data. */
export function applyMetadata(page: PageMetadata) {
  document.title = page.title;
  document.documentElement.lang = page.language || 'en';
  document.head
    .querySelectorAll('link[rel="canonical"], link[hreflang], script[type="application/ld+json"]')
    .forEach((element) => element.remove());
  document.head.querySelectorAll('meta').forEach((element) => {
    const key = element.getAttribute('name') || element.getAttribute('property') || '';
    if (/^(description|keywords|robots|og:.*|twitter:.*|article:.*)$/.test(key)) {
      element.remove();
    }
  });
  const canonical = document.createElement('link');
  canonical.rel = 'canonical';
  canonical.href = page.url;
  document.head.appendChild(canonical);
  for (const alternate of [
    ...(page.alternates || []),
    ...(page.alternates?.length
      ? [
          {
            language: 'x-default',
            url: page.alternates.find((entry) => entry.language === 'en')?.url || page.url,
          },
        ]
      : []),
  ]) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = alternate.language;
    link.href = alternate.url;
    document.head.appendChild(link);
  }
  const published = page.type === 'article' ? articleTime(page.schema, 'datePublished') : undefined;
  const modified = page.type === 'article' ? articleTime(page.schema, 'dateModified') : undefined;
  const values = {
    description: page.description,
    robots: page.noindex ? HIDDEN_ROBOTS : INDEXABLE_ROBOTS,
    'og:type': page.type || 'website',
    ...(published ? { 'article:published_time': published } : {}),
    ...(modified ? { 'article:modified_time': modified } : {}),
    'og:title': page.title,
    'og:description': page.description,
    'og:url': page.url,
    'og:image': page.image,
    'twitter:card': page.image ? 'summary_large_image' : 'summary',
    'twitter:title': page.title,
    'twitter:description': page.description,
    'twitter:image': page.image,
  };
  for (const [key, value] of Object.entries(values)) {
    if (!value) {
      continue;
    }
    const meta = document.createElement('meta');
    meta.setAttribute(
      key.startsWith('og:') || key.startsWith('article:') ? 'property' : 'name',
      key,
    );
    meta.content = value;
    document.head.appendChild(meta);
  }
  if (page.schema) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(page.schema).replaceAll('<', '\\u003c');
    document.head.appendChild(script);
  }
}
